/**
 * FieldGroupsManager — admin UI for repeatable field groups.
 *
 * A field group bundles a set of com_fields together so a form renders them
 * as one unit. is_repeatable=true makes the group expand into "+ Add another"
 * with a Remove control on each instance.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Layers, ArrowUp, ArrowDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useFieldGroups from '@/hooks/useFieldGroups';
import useCustomFields from '@/hooks/useCustomFields';
import { FieldGroup, FieldGroupCreate } from '@/services/api/fieldGroups';
import { RECORD_TYPE_SCOPES, type RecordTypeScope } from '@/services/api/recordTypes';

const EMPTY_DRAFT: FieldGroupCreate = {
  name: '',
  friendly_name: '',
  description: '',
  is_repeatable: true,
  min_instances: null,
  max_instances: null,
  add_button_label: '',
  field_ids: [],
  // TRUS-402: placement is a property of the GROUP. null = the historical
  // default (members + contacts), matching the backend column default.
  record_types: null,
};

interface FieldGroupsManagerProps {
  /** When set, auto-open the ChildrenEditor for this group on mount. Used by
   *  MemberFieldConfigManager → Profile Builder → "Manage members" deep-link
   *  (TRUS-77). Cleared via onFocusConsumed once handled. */
  focusGroupId?: number | null;
  onFocusConsumed?: () => void;
  /** Called after children are saved so the parent (MemberFieldConfigManager)
   *  can refetch its own fieldGroups state and refresh the Profile Builder. */
  onGroupsChanged?: () => void;
}

export default function FieldGroupsManager({ focusGroupId, onFocusConsumed, onGroupsChanged }: FieldGroupsManagerProps = {}) {
  const { groups, loading, error, createGroup, updateGroup, setChildren, deleteGroup, refetch } =
    useFieldGroups();
  // include_group_children: this screen exists to manage repeatable field groups and
  // their children, so it must see the rows entry forms are denied by default (TRUS-302).
  const { fields } = useCustomFields({ include_hidden: true, include_group_children: true });

  const [editing, setEditing] = useState<FieldGroup | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<FieldGroupCreate>(EMPTY_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState<FieldGroup | null>(null);
  const [childrenEditor, setChildrenEditor] = useState<FieldGroup | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Auto-open the ChildrenEditor when the parent passes focusGroupId. Waits
  // for `groups` to load so we can resolve the id → FieldGroup object. Fires
  // once per focusGroupId via onFocusConsumed so a subsequent tab-switch
  // doesn't reopen the dialog.
  useEffect(() => {
    if (focusGroupId == null || !groups) return;
    const target = groups.find((g) => g.id === focusGroupId);
    if (target) {
      setChildrenEditor(target);
      onFocusConsumed?.();
    }
  }, [focusGroupId, groups, onFocusConsumed]);

  const fieldsById = useMemo(() => {
    const map = new Map<number, any>();
    (fields || []).forEach((f: any) => map.set(f.id, f));
    return map;
  }, [fields]);

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setSubmitErr(null);
    setShowCreate(true);
  };

  const openEdit = (group: FieldGroup) => {
    setEditing(group);
    setDraft({
      name: group.name,
      friendly_name: group.friendly_name,
      description: group.description ?? '',
      is_repeatable: group.is_repeatable,
      min_instances: group.min_instances ?? null,
      max_instances: group.max_instances ?? null,
      add_button_label: group.add_button_label ?? '',
      field_ids: group.children.map((c) => c.field_id),
      record_types: group.record_types ?? null,
    });
    setSubmitErr(null);
  };

  const closeForms = () => {
    setShowCreate(false);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setSubmitErr(null);
  };

  const handleSave = async () => {
    setSubmitErr(null);
    if (!draft.name.trim() || !draft.friendly_name.trim()) {
      setSubmitErr('Name and friendly name are required');
      return;
    }
    if (editing) {
      const updated = await updateGroup(editing.id, {
        friendly_name: draft.friendly_name,
        description: draft.description || null,
        is_repeatable: draft.is_repeatable,
        min_instances: draft.min_instances,
        max_instances: draft.max_instances,
        add_button_label: draft.add_button_label || null,
        record_types: draft.record_types && draft.record_types.length > 0 ? draft.record_types : null,
      });
      if (!updated) {
        setSubmitErr('Failed to save group');
        return;
      }
    } else {
      const created = await createGroup(draft);
      if (!created) {
        setSubmitErr('Failed to create group — name must be unique');
        return;
      }
    }
    closeForms();
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const ok = await deleteGroup(confirmDelete.id);
    if (ok) setConfirmDelete(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Repeatable Field Groups
          </CardTitle>
          <CardDescription>
            Bundle related custom fields into a group. Mark a group repeatable so users can
            click "+ Add another" to capture multiple entries (e.g., agency contacts, household
            members).
          </CardDescription>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" /> New Group
        </Button>
      </CardHeader>

      <CardContent>
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded mb-4">{error}</div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading field groups…</div>
        ) : groups.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No field groups yet. Click "New Group" to create one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Children</TableHead>
                <TableHead>Limits</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <div className="font-medium">{g.friendly_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{g.name}</div>
                  </TableCell>
                  <TableCell>
                    {g.is_repeatable ? (
                      <Badge variant="default">Repeatable</Badge>
                    ) : (
                      <Badge variant="outline">Single</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setChildrenEditor(g)}
                      className="text-sm hover:underline"
                    >
                      {g.children.length} field{g.children.length === 1 ? '' : 's'}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {g.min_instances != null || g.max_instances != null
                      ? `${g.min_instances ?? 0} - ${g.max_instances ?? '∞'}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(g)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate || !!editing} onOpenChange={(open) => !open && closeForms()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Field Group' : 'New Field Group'}</DialogTitle>
            <DialogDescription>
              Define a group of fields that can be rendered together — and optionally repeated —
              inside any form.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="fg-name">Internal Name</Label>
              <Input
                id="fg-name"
                value={draft.name}
                disabled={!!editing}
                onChange={(e) =>
                  setDraft({ ...draft, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })
                }
                placeholder="agency_contact"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lowercase, alphanumeric, underscores. Cannot be changed later.
              </p>
            </div>

            <div>
              <Label htmlFor="fg-friendly">Display Name</Label>
              <Input
                id="fg-friendly"
                value={draft.friendly_name}
                onChange={(e) => setDraft({ ...draft, friendly_name: e.target.value })}
                placeholder="Agency Contact"
              />
            </div>

            <div>
              <Label htmlFor="fg-desc">Description (optional)</Label>
              <Textarea
                id="fg-desc"
                value={draft.description ?? ''}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* TRUS-402: which records this group appears on.
                This is the control that was missing. A field inside a group has
                its own "Applies To", but that does NOT move the group — group
                placement is read from the group's own record types, so ticking
                "Household" on a child saved successfully and rendered nothing.
                Setting it here is what actually places the group. */}
            <div>
              <Label>Appears on</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Which records show this group. The fields inside it follow the group — their
                own "Applies To" does not move it.
              </p>
              <div className="flex flex-wrap gap-3">
                {RECORD_TYPE_SCOPES.filter((sc) => sc.assignable).map((sc) => {
                  const current = draft.record_types && draft.record_types.length > 0
                    ? draft.record_types
                    : (['member', 'contact'] as RecordTypeScope[]);
                  const checked = current.includes(sc.value);
                  return (
                    <label key={sc.value} className="flex items-center gap-1.5 text-sm" title={sc.hint}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? current.filter((v) => v !== sc.value)
                            : [...current, sc.value];
                          // An empty selection would place the group nowhere; keep
                          // the historical default rather than hiding it silently.
                          setDraft({ ...draft, record_types: next.length > 0 ? next : null });
                        }}
                        className="rounded"
                      />
                      {sc.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Repeatable</Label>
                <p className="text-xs text-muted-foreground">
                  Show "+ Add another" so users can submit multiple instances.
                </p>
              </div>
              <Switch
                checked={draft.is_repeatable}
                onCheckedChange={(v) => setDraft({ ...draft, is_repeatable: v })}
              />
            </div>

            {draft.is_repeatable && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fg-min">Min Instances</Label>
                  <Input
                    id="fg-min"
                    type="number"
                    min={0}
                    value={draft.min_instances ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        min_instances: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="fg-max">Max Instances</Label>
                  <Input
                    id="fg-max"
                    type="number"
                    min={1}
                    value={draft.max_instances ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        max_instances: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            )}

            {draft.is_repeatable && (
              <div>
                <Label htmlFor="fg-add-label">Add Button Label (optional)</Label>
                <Input
                  id="fg-add-label"
                  value={draft.add_button_label ?? ''}
                  onChange={(e) => setDraft({ ...draft, add_button_label: e.target.value })}
                  placeholder="+ Add another agency"
                />
              </div>
            )}

            {submitErr && (
              <div className="text-sm text-destructive">{submitErr}</div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeForms}>
              Cancel
            </Button>
            <Button onClick={handleSave}>{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Children Editor Dialog */}
      <Dialog open={!!childrenEditor} onOpenChange={(open) => !open && setChildrenEditor(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Group Members — {childrenEditor?.friendly_name}</DialogTitle>
            <DialogDescription>
              Pick which custom fields belong to this group, and reorder them. Removing a field
              here detaches it from the group; the field itself is preserved.
            </DialogDescription>
          </DialogHeader>

          {childrenEditor && (
            <ChildrenEditor
              group={childrenEditor}
              allFields={(fields || []) as any[]}
              onSave={async (ids) => {
                const updated = await setChildren(childrenEditor.id, ids);
                if (updated) {
                  await refetch();
                  setChildrenEditor(null);
                  onGroupsChanged?.();
                }
              }}
              onCancel={() => setChildrenEditor(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Field Group?</DialogTitle>
            <DialogDescription>
              "{confirmDelete?.friendly_name}" will be removed. The {confirmDelete?.children.length}{' '}
              field(s) inside it will be detached but preserved. Forms that reference this group
              will stop rendering it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================================
// Children Editor — local subcomponent
// ============================================================================

function ChildrenEditor({
  group,
  allFields,
  onSave,
  onCancel,
}: {
  group: FieldGroup;
  allFields: any[];
  onSave: (fieldIds: number[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<number[]>(group.children.map((c) => c.field_id));
  const [filter, setFilter] = useState('');

  const fieldsById = useMemo(() => {
    const map = new Map<number, any>();
    allFields.forEach((f) => map.set(f.id, f));
    return map;
  }, [allFields]);

  const available = allFields.filter(
    (f) =>
      !selected.includes(f.id) &&
      // Don't show fields already attached to a different group
      (f.field_group_id == null || f.field_group_id === group.id) &&
      (!filter ||
        f.friendly_name.toLowerCase().includes(filter.toLowerCase()) ||
        f.name.toLowerCase().includes(filter.toLowerCase()))
  );

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...selected];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-2 block">In this group ({selected.length})</Label>
        {selected.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center border rounded">
            No fields in this group yet.
          </div>
        ) : (
          <div className="border rounded divide-y">
            {selected.map((id, idx) => {
              const f = fieldsById.get(id);
              return (
                <div key={id} className="flex items-center justify-between p-2">
                  <div className="text-sm">
                    <div className="font-medium">{f?.friendly_name ?? `Field #${id}`}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {f?.name} · {f?.type}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={idx === 0}
                      onClick={() => move(idx, -1)}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={idx === selected.length - 1}
                      onClick={() => move(idx, 1)}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelected(selected.filter((x) => x !== id))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <Label className="mb-2 block">Add custom fields to this group</Label>
        <Input
          placeholder="Search fields…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-2"
        />
        <div className="border rounded max-h-60 overflow-y-auto divide-y">
          {available.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No more fields available.
            </div>
          ) : (
            available.map((f) => (
              <button
                type="button"
                key={f.id}
                onClick={() => setSelected([...selected, f.id])}
                className="w-full flex items-center justify-between p-2 hover:bg-muted/50 text-left"
              >
                <div>
                  <div className="text-sm font-medium">{f.friendly_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {f.name} · {f.type}
                  </div>
                </div>
                <Plus className="h-4 w-4 text-muted-foreground" />
              </button>
            ))
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(selected)}>Save Members</Button>
      </DialogFooter>
    </div>
  );
}
